import React from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import LandingButton from './LandingButton';
import epkCover from '../../images/landing_page_redo/EpkCoverBanner.webp';
import epkAudience from '../../images/landing_page_redo/EpkAudience.webp';

const LandingHero = () => {
  const { t } = useTranslation();

  const highlights = [
    t('Free to use'),
    t('Start with just a title and a synopsis'),
    t('Share with a single link'),
  ];

  return (
    <section
      aria-labelledby="landing-hero-title"
      className="tw-relative tw-overflow-hidden tw-bg-midnight tw-px-4 tw-pb-8 tw-pt-4 sm:tw-px-6 md:tw-pb-16 md:tw-pt-10"
    >
      {/* Decorative glow */}
      <div
        aria-hidden="true"
        className="tw-pointer-events-none tw-absolute tw-right-[-10%] tw-top-[-10%] tw-h-[480px] tw-w-[480px] tw-rounded-full tw-bg-[#FF43A7]/15 tw-blur-[120px]"
      />

      <div className="tw-relative tw-mx-auto tw-grid tw-max-w-7xl tw-items-center tw-gap-14 lg:tw-grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:tw-gap-16">
        <div className="tw-text-center motion-safe:tw-animate-fade-in lg:tw-text-left">
          <p className="tw-mb-5 tw-text-sm tw-font-bold tw-uppercase tw-tracking-[0.2em] tw-text-[#FF43A7]">
            {t('Industry Standard EPK Builder')}
          </p>

          <h1
            id="landing-hero-title"
            className="tw-m-0 tw-text-4xl tw-font-extrabold tw-leading-[1.05] tw-tracking-tight tw-text-white [text-wrap:balance] sm:tw-text-5xl xl:tw-text-6xl"
          >
            {t("Manage your film's entire press kit, all in one place.")}
          </h1>

          <p className="tw-mx-auto tw-mb-0 tw-mt-6 tw-max-w-xl tw-text-lg tw-leading-relaxed tw-text-white/70 lg:tw-mx-0 lg:tw-text-xl">
            {t("Stop emailing massive PDF files and broken Google Drive links. KinoKlik lets you build stunning, interactive Electronic Press Kits (EPKs) to pitch investors, secure distribution, and build your audience.")}
          </p>

          <div className="tw-mt-8 tw-flex tw-flex-col tw-gap-3 sm:tw-flex-row sm:tw-justify-center lg:tw-justify-start">
            <LandingButton to="/signup">{t('Build Your Free EPK')}</LandingButton>
            <LandingButton to="/catalog" variant="secondary">{t('Browse our catalogue')}</LandingButton>
          </div>

          <ul className="tw-m-0 tw-mt-8 tw-flex tw-list-none tw-flex-col tw-items-center tw-gap-x-6 tw-gap-y-2 tw-p-0 tw-text-sm tw-text-white/70 sm:tw-flex-row sm:tw-flex-wrap sm:tw-justify-center lg:tw-justify-start">
            {highlights.map((item) => (
              <li key={item} className="tw-flex tw-items-center tw-gap-2">
                <FontAwesomeIcon icon={faCheck} className="tw-text-[#FF43A7]" aria-hidden="true" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        {/* Product preview: stays dark in both themes, like the EPK page it shows */}
        <div className="tw-relative tw-pb-10 sm:tw-pb-12">
          <figure className="kk-dark-section tw-m-0 tw-overflow-hidden tw-rounded-2xl tw-border tw-border-solid tw-border-white/25 tw-bg-[#14002A] tw-shadow-[0_30px_80px_rgba(0,0,0,0.45)]">
            <div aria-hidden="true" className="tw-flex tw-gap-1.5 tw-border-0 tw-border-b tw-border-solid tw-border-white/25 tw-px-4 tw-py-3">
              <span className="tw-h-2.5 tw-w-2.5 tw-rounded-full tw-bg-[#FF5F57]" />
              <span className="tw-h-2.5 tw-w-2.5 tw-rounded-full tw-bg-[#FEBC2E]" />
              <span className="tw-h-2.5 tw-w-2.5 tw-rounded-full tw-bg-[#28C840]" />
            </div>
            <img
              src={epkCover}
              width={1600}
              height={750}
              alt={t('A KinoKlik EPK cover page showing the film banner, poster and title')}
              fetchpriority="high"
              decoding="async"
              className="tw-block tw-h-auto tw-w-full"
            />
          </figure>
          <img
            src={epkAudience}
            width={1600}
            height={104}
            alt={t("Total audience reach combined from the cast and crew's social followers")}
            loading="lazy"
            decoding="async"
            className="tw-absolute tw-bottom-0 tw-left-4 tw-right-4 tw-h-auto tw-w-[calc(100%-2rem)] tw-rounded-xl tw-border tw-border-solid tw-border-white/25 tw-shadow-[0_20px_50px_rgba(0,0,0,0.5)] sm:tw-left-8 sm:tw-w-[85%]"
          />
        </div>
      </div>
    </section>
  );
};

export default LandingHero;
